import { EstateLevel } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export default function useGetHotelDetails(hotelId, setIsLockedRoom) {
  const { t } = useTranslation("hotel");

  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [data, setData] = useState(null);
  const [roomData, setRoomData] = useState(null);

  const onClose = useCallback(() => {
    setData(null);
    setRoomData(null);
    GameMpProvider.closeCurrentInterface(true);
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);

    try {
      const roomResponse = await EstateMpProvider.getHotelRoomDetailsByHotelId(hotelId);

      if (roomResponse instanceof RpcMpProviderSuccessResponse) {
        if (roomResponse?.data && Object.keys(roomResponse.data).length > 0) {
          setRoomData(roomResponse.data);
          setIsLockedRoom(roomResponse.data.isLocked);
        } else {
          const hotelResponse = await EstateMpProvider.getHotelDetails(hotelId);

          if (hotelResponse instanceof RpcMpProviderSuccessResponse) {
            setData(hotelResponse.data);
          } else {
            console.log(hotelResponse);
            toast.error(t("hotel.toast.loadingHotelError"));
          }
        }
      } else {
        console.log(roomResponse);
        toast.error(t("hotel.toast.loadingRoomError"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("hotel.toast.loadingSystemError"));
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, setIsLockedRoom, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onPay = useCallback(
    async (id: number, level: EstateLevel, paidDays: number, paymentType: PaymentType) => {
      setIsPaymentLoading(true);

      await EstateMpProvider.hotelRoomRent(id, level, paidDays, paymentType)
        .then((response) => {
          if (response instanceof RpcMpProviderSuccessResponse) {
            toast.success(t("hotel.toast.paySuccess"));
            load();
          } else {
            console.error(response);
            if (response.message === "NOT_ENOUGH_MONEY_ERROR") {
              toast.error(t("hotel.errors.NOT_ENOUGH_MONEY_ERROR"));
            } else {
              toast.error(t("hotel.toast.payError"));
            }
          }
        })
        .catch((e) => {
          console.error(e);
          toast.error(t("hotel.toast.paySystemError"));
        })
        .finally(() => {
          setIsPaymentLoading(false);
        });
    },
    [load, t],
  );

  return { data, roomData, isLoading, onClose, isPaymentLoading, onPay, load };
}
